/** @format */

import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import {
  GTGT_FILTER_NAME,
  GTGT_FILTER_VALUE_DSG_PL,
  GTGT_FILTER_VALUE_GG,
  RATINGS_FILTER_ID,
  shippingOptions,
} from '@plp/data-access/constants';
import { HmfActionTextComponent, HmfGlobalChipsComponent } from '@dsg-tech/homefield-angular';
import { KeyValuePipe, TitleCasePipe } from '@angular/common';
import { ProductListLayoutService, ProductListUIService } from '@plp/data-access/services';
import {
  filterChipsClearAllClicked,
  selectProductListCategoryDetails,
  selectSelectedFilters,
  selectedFilterRemoved,
} from '@plp/data-access/state';
import { selectUserStoreInfo, selectUserZipCode } from '@state/data-access';

import { Chain } from '@shared/data-access/interfaces';
import { HomefieldTextLinkModule } from '@ecomm-patterns-components';
import { Store } from '@ngrx/store';
import { UtilityService } from '@shared/data-access/services';

@Component({
  selector: 'plp-components-filter-chips',
  imports: [KeyValuePipe, HmfGlobalChipsComponent, HomefieldTextLinkModule, HmfActionTextComponent],
  providers: [TitleCasePipe],
  templateUrl: './filter-chips.component.html',
  styleUrl: './filter-chips.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterChipsComponent {
  private readonly chain = inject(Chain);
  private readonly store = inject(Store);
  private readonly productListUIService = inject(ProductListUIService);
  private readonly titleCasePipe = inject(TitleCasePipe);
  private readonly productListLayoutService = inject(ProductListLayoutService);
  private readonly utilityService = inject(UtilityService);

  readonly wrapChips = input(false);

  readonly categoryDetails = this.store.selectSignal(selectProductListCategoryDetails);
  readonly selectedStoreInfo = this.store.selectSignal(selectUserStoreInfo);
  readonly userZipCode = this.store.selectSignal(selectUserZipCode);
  readonly selectedFilters = this.store.selectSignal(selectSelectedFilters);

  private readonly filterDisplayTextMap = computed(() => {
    const selectedFilters = this.selectedFilters();
    const result: Record<string, { label: string; subtitle?: string; icon?: string }> = {
      ...shippingOptions,
    };

    result['ISA'] = { ...result['ISA'], label: 'Shop' };

    if (selectedFilters['dickssportinggoodsofferprice']?.length) {
      selectedFilters['dickssportinggoodsofferprice'].forEach((priceRange) => {
        result[priceRange] = {
          label: this.productListUIService.addSymbolForPriceDisplay(priceRange),
        };
      });
    }

    if (selectedFilters[GTGT_FILTER_NAME]?.length) {
      result['Y'] = {
        label:
          this.chain.chainIdentifierAbbr === 'gg' ? GTGT_FILTER_VALUE_GG : GTGT_FILTER_VALUE_DSG_PL,
      };
    }
    return result;
  });

  readonly isSelectedFilterEmpty = computed(() => {
    return Object.keys(this.selectedFilters()).length === 0;
  });

  readonly formattedSelectedFilters = computed(() => {
    const labelMap = this.filterDisplayTextMap();
    const categoryDetails = this.categoryDetails();
    const selectedStoreInfo = this.selectedStoreInfo();
    const userZipCode = this.userZipCode();

    return Object.entries(this.selectedFilters()).reduce(
      (acc, [key, values]) => {
        const formattedValues = values.map((value) => {
          let label = value;

          const mappedLabel = labelMap?.[value]?.label;

          if (mappedLabel) {
            label = mappedLabel;
          } else if (key === 'facetCatgroup') {
            label = categoryDetails?.[value]?.name || label;
          } else if (key === RATINGS_FILTER_ID) {
            const rating = label.replace('+', '');
            label = rating === '5' ? `${rating} stars` : `${rating} stars & up`;
          }
          if (key === 'facetStore') {
            if (selectedStoreInfo?.name && (value === 'BOPIS' || value === 'ISA')) {
              label += ` at ${this.titleCasePipe.transform(selectedStoreInfo.name)}`;
            } else if (userZipCode && (value === 'SHIP' || value === 'SDD')) {
              label += ` to ${userZipCode}`;
            }
          }

          return { value, label };
        });

        return { ...acc, [key]: formattedValues };
      },
      {} as Record<string, { value: string; label: string }[]>,
    );
  });

  removeFilter(filterType: string, filterLabel: string): void {
    const currentFilters = this.selectedFilters()?.[filterType];

    if (!currentFilters?.length) {
      return;
    }

    this.store.dispatch(
      selectedFilterRemoved({
        filterType,
        filterLabel,
      }),
    );
  }

  removeFilters(): void {
    this.productListLayoutService.scrollToTop();

    this.utilityService.yieldTask(() => {
      this.store.dispatch(filterChipsClearAllClicked());
    });
  }
}
